import DOMPurify from "dompurify";

/**
 * Tags a rendered note may contain. Stored note bodies are mostly plain text; the
 * markup that actually occurs in production is <b>/<strong> emitted by a few
 * templates, plus the block/list tags a rich-text edit can produce. Anything else
 * (script, iframe, form, img, svg, a, table, ...) is dropped, text content kept.
 *
 * Mirrors NOTE_ALLOWED_TAGS in backend/utils/sanitizer.py — change both together.
 */
export const NOTE_ALLOWED_TAGS = [
  "p",
  "br",
  "div",
  "span",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "sub",
  "sup",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
  "code",
  "hr",
];

/** No attributes at all by default: no event handlers, no href/src, no style. */
export const NOTE_ALLOWED_ATTR: string[] = [];

export type SanitizeHtmlOptions = {
  /**
   * Keep `style="..."` attributes. Only for markup produced by our own formatter
   * from already-escaped text (SuperdocThread.formatMarkdown), where the inline
   * styles are ours and the sanitizer is a second line of defence, not the first.
   * Never set this for stored note bodies.
   */
  allowInlineStyles?: boolean;
};

/**
 * Sanitize an HTML fragment before it reaches `dangerouslySetInnerHTML`.
 *
 * Plain text renders unchanged: stray `<`, `>` and `&` come back entity-encoded
 * in the returned string, which the browser decodes to the same characters.
 */
export function sanitizeHtml(html: string | null | undefined, options: SanitizeHtmlOptions = {}): string {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: NOTE_ALLOWED_TAGS,
    ALLOWED_ATTR: options.allowInlineStyles ? ["style"] : NOTE_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    // Belt and braces: these never render even if the allowlist above grows.
    FORBID_TAGS: [
      "script",
      "style",
      "iframe",
      "frame",
      "object",
      "embed",
      "form",
      "input",
      "svg",
      "math",
      "link",
      "meta",
      "base",
    ],
    FORBID_ATTR: ["onerror", "onload", "onclick", "href", "src", "srcset", "action", "formaction", "xlink:href"],
  });
}
